import { EXTERNAL_SCOPE_TYPES } from '../../models/messaging-user';
import {
  MESSAGES_PER_PAGE,
  pageNumberInfinityModel,
  pageNumberInfinityOptions,
} from '../../utils/messaging/infinity-model';
import { MODEL_NAMES } from '../../constants/index';
import { A as emberArray } from '@ember/array';
import { filterSystemAndCurrentUser } from '../../utils/messaging/user';
import { getInactiveReasonsFromResponse } from '../../utils/messaging/conversation';
import { service } from '@ember/service';
import { task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import MessagingRoutesBaseHandler from './base-handler.js';

export default class MessagingRoutesConversationHandler extends MessagingRoutesBaseHandler {
  @service('spds/toast') toast;
  @service infinity;
  @service intl;
  @service messaging;
  @service messagingLogging;
  @service messagingNavigation;
  @service store;
  @service('websocket/messagingWebsocket') messagingWebsocket;

  @tracked conversation;
  @tracked inactiveReasons = [];
  @tracked messages = emberArray();

  constructor(owner, options) {
    super(owner, options);
    this.conversation = options.conversation;
  }

  onVisit = task({ drop: true }, async () => {
    try {
      if (this.conversation.isNew || this.conversation.inactive) {
        await this.fetchAndValidateUsers.perform(this.conversation);
        this.#subscribeToActiveConversation();
      }

      if (this.conversation.isNew && !this.inactiveReasons.length) {
        await this.saveConversation(this.conversation);
      }

      this.getMessages.perform();
    } catch (e) {
      this.messagingLogging.error(e, {
        context: 'conversation onVisit',
        conversationId: this.conversation?.id,
      });
      this.#handleConversationError();
      this.messagingNavigation.toConversationList();
    }
  });

  onLeave = task({ drop: true }, async () => {
    this.#unsubscribeFromActiveConversation();
  });

  replaceConversation(newConversation, messages) {
    this.#unsubscribeFromActiveConversation();
    this.conversation = newConversation;
    this.options = { ...this.options, conversation: newConversation };
    this.resetInactiveReasons();
    this.#subscribeToActiveConversation();
    this.getMessages.cancelAll();

    if (messages) {
      this.messages = messages;
      return;
    }

    this.messages = emberArray();
    this.getMessages.perform();
  }

  getMessages = task({ drop: true }, async () => {
    if (this.conversation.isNew) {
      return;
    }

    this.messages = await this.initMessagesInfinityModel.perform();
  });

  loadMoreMessages = task({ drop: true }, async model => {
    try {
      await this.infinity.loadNextPage(model);
    } catch (err) {
      this.messagingLogging.error(err, {
        context: 'conversation loadMoreMessages',
        conversationId: this.conversation?.id,
      });
      this.#handleConversationError();
    }
  });

  fetchAndValidateUsers = task({ drop: true }, async conversation => {
    const response = await this.store
      .adapterFor(MODEL_NAMES.CONVERSATION)
      .fetchAndValidateUsers(conversation);

    if (response.ok) {
      await this.#handleUserFetchSuccess(response, conversation);
    } else {
      await this.#handleUserFetchError(response);
    }
  });

  saveNewConversation = task({ drop: true }, async (conversation, firstMessage) => {
    if (!conversation.isNew) {
      return;
    }

    await this.saveConversation(conversation);
    const infinityModel = await this.initMessagesInfinityModel.perform();
    infinityModel.unshiftObject(firstMessage);
    this.messages = infinityModel;
  });

  initMessagesInfinityModel = task({ drop: true }, async () => {
    return await this.infinity.model(
      MODEL_NAMES.MESSAGE,
      {
        conversationId: this.conversation.id,
        include: 'conversation,user',
        filter: {
          'include_system': this.messaging.currentProfile.isClinician,
        },
        ...pageNumberInfinityOptions(MESSAGES_PER_PAGE),
      },
      pageNumberInfinityModel(MESSAGES_PER_PAGE)
    );
  });

  // This is a workaround to avoid a bug between ember-concurrency and ember-data. Trying to await a
  // save inside of a task can cause an error around `__ec_cancel` and PromiseProxy deprecation
  // so we move it outside the task.
  async saveConversation(conversation) {
    await conversation.save();
  }

  resetInactiveReasons() {
    this.inactiveReasons = [];
  }

  get filteredUsers() {
    return filterSystemAndCurrentUser(
      this.conversation.users || [],
      this.messaging.currentProfile?.user
    );
  }

  #subscribeToActiveConversation() {
    if (!this.conversation || !this.messagingWebsocket.isConnected) {
      return;
    }

    this.filteredUsers.forEach(user => {
      // Only subscribe to Client contacts, not User contacts
      if (user.externalScope === EXTERNAL_SCOPE_TYPES.client) {
        this.messagingWebsocket.subscribeToActiveConversation(user.externalId, user.organizationId);
      }
    });
  }

  #unsubscribeFromActiveConversation() {
    if (!this.conversation || !this.messagingWebsocket) {
      return;
    }

    this.filteredUsers.forEach(user => {
      if (user.externalId && user.organizationId) {
        this.messagingWebsocket.unsubscribeFromActiveConversation(
          user.externalId,
          user.organizationId
        );
      }
    });
  }

  async #handleUserFetchSuccess(response, conversation) {
    this.resetInactiveReasons();
    const json = await response.json();
    await this.#updateUserRecords(json, conversation);
  }

  async #updateUserRecords(json) {
    const unsavedUsers = this.store.peekAll(MODEL_NAMES.USER).filter(user => user.isNew);

    for (const jsonUser of json.data) {
      const normalizedUser = this.store.normalize(MODEL_NAMES.USER, jsonUser);
      const { externalId } = normalizedUser.data.attributes;

      const matchingUnsavedUser = unsavedUsers.find(user => user.externalId === externalId);

      if (matchingUnsavedUser) {
        // Since the user was already created in the backend, we pass the data as an adapter option
        await matchingUnsavedUser.save({ adapterOptions: { data: jsonUser } });
      } else {
        this.store.push(normalizedUser);
      }
    }
  }

  async #handleUserFetchError(response) {
    if (response.status !== 422) {
      const error = new Error(this.intl.t('emc.general_error'));
      error.context = 'fetchAndValidateUsers';
      error.status = response.status;
      error.conversationId = this.conversation?.id;
      throw error;
    }

    this.inactiveReasons = await getInactiveReasonsFromResponse(response);
  }

  #handleConversationError() {
    this.toast.error(this.intl.t('emc.general_error'));
  }
}
